import Plausible, { PlausibleOptions } from 'plausible-tracker'
import type { History } from 'history'

import config from '@sketch/env-config'
import {
  AnalyticsEventName,
  AnalyticsEventPropsOf,
  AnalyticsEventNameWithoutProps,
  AnalyticsEventNameWithProps,
  Scalar,
} from './types'
import { getTemplateURLWithParams } from '../routes'

const defaultLocation = { pathname: '', search: '' }
let previousLocation: Pick<Location, 'pathname' | 'search'> = defaultLocation

const isJestRunning = () => process.env.JEST_WORKER_ID !== undefined

const isPlausibleEnabled = () =>
  Boolean(config.analytics_id) && !isJestRunning()

/**
 * By default analytics are not tracked when running on localhost but you can enable it
 * by using REACT_APP_TRACK_LOCALHOST env variable.
 *
 * `REACT_APP_TRACK_LOCALHOST=true pnpm run start`
 */
const trackLocalhost = process.env.REACT_APP_TRACK_LOCALHOST === 'true'
const plausible = Plausible({
  domain: config.analytics_id,
  apiHost: config.analytics_uri,
  /**  window.__trackLocalhost is set from Cypress tests */
  trackLocalhost: window.__trackLocalhost || trackLocalhost,
})

export const initializePlausible = (history: History) => {
  if (!isPlausibleEnabled()) {
    return
  }

  const setPageView = (
    location: Pick<Location, 'pathname' | 'search'> = defaultLocation
  ) => {
    const isSameLocation =
      location.pathname === previousLocation.pathname &&
      location.search === previousLocation.search

    if (location.pathname === '/' || isSameLocation) {
      // We don't track '/' route as it is the initial
      // pathname for the router, but doesn't match an actual
      // route within the Cloud web application
      return
    }

    plausible.trackPageview({
      url: getTemplateURLWithParams(location),
    })

    previousLocation = location
  }

  // We need to set the first page where the user lands.
  // history.listen will then catch the url changes from regular
  // navigation from this point on.
  setPageView(history.location)
  history.listen(setPageView)
}

export function trackEvent<TEventName extends AnalyticsEventNameWithProps>(
  eventName: TEventName,
  eventProps: AnalyticsEventPropsOf<TEventName>,
  options?: PlausibleOptions
): void

export function trackEvent<TEventName extends AnalyticsEventNameWithoutProps>(
  eventName: TEventName,
  options?: PlausibleOptions
): void

/**
 * trackEvent
 *
 * Send a custom event with custom properties
 * to track goals defined in Plausible
 *
 * Usage:
 * ```
 *  import { trackEvent } from 'utils/analytics'
 *  trackEvent('EventName', { eventProp: 'eventPropValue' })
 * ```
 */
export function trackEvent<
  TEventName extends AnalyticsEventName,
  TEventProps extends Record<string, Scalar>
>(
  /**
   * eventName should be pre-defined in constants/analyticsEvents.ts
   */
  eventName: TEventName,
  eventProps?: TEventProps,
  options?: PlausibleOptions
) {
  if (!isPlausibleEnabled()) {
    return
  }

  plausible.trackEvent(
    eventName,
    { props: (eventProps ?? {}) as Record<string, any> },
    { ...options, url: getTemplateURLWithParams(window.location) }
  )
}
